import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, Cpu, ClipboardCheck, FileWarning,
  FolderKanban, FolderTree, Edit2, Save, X,
  Wrench, FlaskConical, Award, ShieldAlert,
} from 'lucide-react'
import { equipmentApi } from '../services/equipmentApi'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { StatusBadge } from '../components/common/StatusBadge'
import { PriorityBadge } from '../components/common/PriorityBadge'
import { ProgressBar } from '../components/common/ProgressBar'
import { EmptyState } from '../components/common/EmptyState'
import type {
  EquipmentStatus, EquipmentUpdate, ChecklistStatus,
  PunchItemPriority, IssueStatus,
} from '../types'

interface EquipmentDetail {
  id: number
  systemId: number
  projectId: number
  tag: string
  name: string
  description: string | null
  manufacturer: string | null
  model: string | null
  serialNumber: string | null
  location: string | null
  status: EquipmentStatus
  installDate: string | null
  createdAt: string
  updatedAt: string
  system: { code: string; name: string } | null
  project: { projectNumber: string; projectName: string } | null
  checklists: {
    id: number
    name: string
    templateName: string
    status: ChecklistStatus
    completionPercentage: number
    assignedTo: string | null
  }[]
  issues: {
    id: number
    issueNumber: string
    title: string
    type: string
    category: string
    priority: PunchItemPriority
    status: IssueStatus
    phase: string | null
  }[]
}

type TabKey = 'overview' | 'checklists' | 'issues'

interface EditFormState {
  tag: string
  name: string
  description: string
  manufacturer: string
  model: string
  serialNumber: string
  location: string
  installDate: string
}

const STATUS_FLOW: EquipmentStatus[] = ['Pending', 'Installed', 'Tested', 'Commissioned']

function getNextStatuses(current: EquipmentStatus): { label: string; status: EquipmentStatus; icon: React.ReactNode }[] {
  const buttons: { label: string; status: EquipmentStatus; icon: React.ReactNode }[] = []
  const currentIndex = STATUS_FLOW.indexOf(current)

  if (current === 'Deficient') {
    // From Deficient, allow going back to any normal status
    buttons.push({ label: 'Mark as Pending', status: 'Pending', icon: <Cpu size={14} /> })
    buttons.push({ label: 'Mark as Installed', status: 'Installed', icon: <Wrench size={14} /> })
    buttons.push({ label: 'Mark as Tested', status: 'Tested', icon: <FlaskConical size={14} /> })
    buttons.push({ label: 'Mark as Commissioned', status: 'Commissioned', icon: <Award size={14} /> })
  } else {
    // Show next step in the flow
    if (currentIndex >= 0 && currentIndex < STATUS_FLOW.length - 1) {
      const next = STATUS_FLOW[currentIndex + 1]!
      const labels: Record<string, { label: string; icon: React.ReactNode }> = {
        Installed: { label: 'Mark as Installed', icon: <Wrench size={14} /> },
        Tested: { label: 'Mark as Tested', icon: <FlaskConical size={14} /> },
        Commissioned: { label: 'Mark as Commissioned', icon: <Award size={14} /> },
      }
      if (labels[next]) {
        buttons.push({ label: labels[next]!.label, status: next, icon: labels[next]!.icon })
      }
    }
    // Always allow "Mark as Deficient" from any non-deficient state
    buttons.push({ label: 'Mark as Deficient', status: 'Deficient', icon: <ShieldAlert size={14} /> })
  }

  return buttons
}

const EMPTY_EDIT_FORM: EditFormState = {
  tag: '',
  name: '',
  description: '',
  manufacturer: '',
  model: '',
  serialNumber: '',
  location: '',
  installDate: '',
}

export function EquipmentDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [equipment, setEquipment] = useState<EquipmentDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabKey>('overview')

  // Edit mode state
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState<EditFormState>(EMPTY_EDIT_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Status transition state
  const [statusUpdating, setStatusUpdating] = useState(false)

  const load = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      setError(null)
      const data = await equipmentApi.getById(id)
      setEquipment(data as unknown as EquipmentDetail)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load equipment')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  // Populate edit form when entering edit mode
  const startEditing = () => {
    if (!equipment) return
    setEditForm({
      tag: equipment.tag,
      name: equipment.name,
      description: equipment.description || '',
      manufacturer: equipment.manufacturer || '',
      model: equipment.model || '',
      serialNumber: equipment.serialNumber || '',
      location: equipment.location || '',
      installDate: equipment.installDate ? String(equipment.installDate).split('T')[0] ?? '' : '',
    })
    setSaveError(null)
    setEditing(true)
  }

  const cancelEditing = () => {
    setEditing(false)
    setSaveError(null)
  }

  const handleEditChange = (field: keyof EditFormState, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }))
  }

  const saveEdit = async () => {
    if (!id || !equipment) return
    if (!editForm.tag.trim() || !editForm.name.trim()) {
      setSaveError('Tag and Name are required.')
      return
    }
    try {
      setSaving(true)
      setSaveError(null)
      const payload: EquipmentUpdate = {
        tag: editForm.tag.trim(),
        name: editForm.name.trim(),
        description: editForm.description.trim() || undefined,
        manufacturer: editForm.manufacturer.trim() || undefined,
        model: editForm.model.trim() || undefined,
        serialNumber: editForm.serialNumber.trim() || undefined,
        location: editForm.location.trim() || undefined,
        installDate: editForm.installDate || null,
      }
      await equipmentApi.update(id, payload)
      setEditing(false)
      await load()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  const handleStatusChange = async (newStatus: EquipmentStatus) => {
    if (!id) return
    try {
      setStatusUpdating(true)
      await equipmentApi.update(id, { status: newStatus })
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status')
    } finally {
      setStatusUpdating(false)
    }
  }

  if (loading) return <LoadingSpinner message="Loading equipment..." />
  if (error && !equipment) {
    return (
      <div className="page-container">
        <div className="info-card danger">
          <p style={{ margin: 0 }}>{error}</p>
          <button className="btn btn-sm btn-secondary mt-3" onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    )
  }
  if (!equipment) return null

  const openIssues = equipment.issues.filter(i => i.status === 'Open' || i.status === 'InProgress' || i.status === 'PendingResponse').length
  const completedChecklists = equipment.checklists.filter(c => c.status === 'Complete').length

  const statusButtons = getNextStatuses(equipment.status)

  const tabs: { key: TabKey; label: string; icon: React.ReactNode; count?: number }[] = [
    { key: 'overview', label: 'Overview', icon: null },
    { key: 'checklists', label: 'Checklists', icon: <ClipboardCheck size={14} />, count: equipment.checklists.length },
    { key: 'issues', label: 'Issues', icon: <FileWarning size={14} />, count: equipment.issues.length },
  ]

  return (
    <div className="page-container animate-fade-in">
      <div className="breadcrumbs">
        <Link to="/projects">Projects</Link>
        <span className="breadcrumbs-separator">/</span>
        {equipment.project && (
          <>
            <Link to={`/projects/${equipment.projectId}`}>{equipment.project.projectNumber}</Link>
            <span className="breadcrumbs-separator">/</span>
          </>
        )}
        <span className="breadcrumbs-current">{equipment.tag}</span>
      </div>

      <div className="page-header">
        <div className="d-flex align-center gap-3">
          <button className="btn btn-ghost btn-icon" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </button>
          <Cpu size={24} className="text-accent" />
          <div>
            <h1>{equipment.tag} — {equipment.name}</h1>
            <p className="page-subtitle">
              {equipment.system && <><FolderTree size={14} style={{ verticalAlign: 'middle' }} /> {equipment.system.code} — {equipment.system.name}</>}
              {equipment.project && <> &bull; <FolderKanban size={14} style={{ verticalAlign: 'middle' }} /> {equipment.project.projectName}</>}
            </p>
          </div>
          <StatusBadge status={equipment.status} />
        </div>
        <div className="d-flex align-center gap-2" style={{ marginTop: '8px', flexWrap: 'wrap' }}>
          {statusButtons.map((btn) => (
            <button
              key={btn.status}
              className={`btn btn-sm ${btn.status === 'Deficient' ? 'btn-danger' : 'btn-secondary'}`}
              onClick={() => handleStatusChange(btn.status)}
              disabled={statusUpdating}
            >
              {btn.icon}
              {btn.label}
            </button>
          ))}
          {activeTab === 'overview' && !editing && (
            <button className="btn btn-sm btn-primary" onClick={startEditing}>
              <Edit2 size={14} />
              Edit
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="info-card danger mb-4">
          <p style={{ margin: 0 }}>{error}</p>
        </div>
      )}

      <div className="tabs mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon}
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="badge badge-gray-light" style={{ marginLeft: '6px', fontSize: 'var(--font-size-xs)' }}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          <div className="metrics-row mb-6">
            <div className="metric-card green">
              <div className="metric-header"><span className="metric-label">Checklists</span></div>
              <div className="metric-value">{completedChecklists}/{equipment.checklists.length}</div>
              <div className="metric-subtext">completed</div>
            </div>
            <div className="metric-card amber" style={{ cursor: 'pointer' }} onClick={() => setActiveTab('issues')}>
              <div className="metric-header"><span className="metric-label">Open Issues</span></div>
              <div className="metric-value">{openIssues}</div>
              <div className="metric-subtext">{equipment.issues.length} total</div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3>Equipment Details</h3>
              {editing && (
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-secondary" onClick={cancelEditing} disabled={saving}>
                    <X size={14} />
                    Cancel
                  </button>
                  <button className="btn btn-sm btn-primary" onClick={saveEdit} disabled={saving}>
                    <Save size={14} />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
            <div className="card-body">
              {saveError && (
                <div className="info-card danger mb-4">
                  <p style={{ margin: 0 }}>{saveError}</p>
                </div>
              )}

              {editing ? (
                <div className="form-grid">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Tag *</label>
                      <input
                        className="form-input"
                        value={editForm.tag}
                        onChange={(e) => handleEditChange('tag', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Name *</label>
                      <input
                        className="form-input"
                        value={editForm.name}
                        onChange={(e) => handleEditChange('name', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-input"
                      rows={3}
                      value={editForm.description}
                      onChange={(e) => handleEditChange('description', e.target.value)}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Manufacturer</label>
                      <input
                        className="form-input"
                        value={editForm.manufacturer}
                        onChange={(e) => handleEditChange('manufacturer', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Model</label>
                      <input
                        className="form-input"
                        value={editForm.model}
                        onChange={(e) => handleEditChange('model', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Serial Number</label>
                      <input
                        className="form-input"
                        value={editForm.serialNumber}
                        onChange={(e) => handleEditChange('serialNumber', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Location</label>
                      <input
                        className="form-input"
                        value={editForm.location}
                        onChange={(e) => handleEditChange('location', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Install Date</label>
                      <input
                        type="date"
                        className="form-input"
                        value={editForm.installDate}
                        onChange={(e) => handleEditChange('installDate', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <div className="detail-value"><StatusBadge status={equipment.status} /></div>
                      <span className="text-secondary-color" style={{ fontSize: 'var(--font-size-xs)' }}>
                        Use status buttons in the header to change status
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="detail-grid">
                  <div className="detail-row"><span className="detail-label">Tag</span><span className="detail-value">{equipment.tag}</span></div>
                  <div className="detail-row"><span className="detail-label">Name</span><span className="detail-value">{equipment.name}</span></div>
                  <div className="detail-row"><span className="detail-label">Description</span><span className="detail-value">{equipment.description || 'No description'}</span></div>
                  <div className="detail-row"><span className="detail-label">Manufacturer</span><span className="detail-value">{equipment.manufacturer || '-'}</span></div>
                  <div className="detail-row"><span className="detail-label">Model</span><span className="detail-value">{equipment.model || '-'}</span></div>
                  <div className="detail-row"><span className="detail-label">Serial Number</span><span className="detail-value">{equipment.serialNumber || '-'}</span></div>
                  <div className="detail-row"><span className="detail-label">Location</span><span className="detail-value">{equipment.location || '-'}</span></div>
                  <div className="detail-row"><span className="detail-label">Install Date</span><span className="detail-value">{equipment.installDate ? new Date(equipment.installDate).toLocaleDateString() : 'Not set'}</span></div>
                  <div className="detail-row"><span className="detail-label">Status</span><span className="detail-value"><StatusBadge status={equipment.status} /></span></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Checklists Tab */}
      {activeTab === 'checklists' && (
        <div>
          <h3 className="mb-4">Checklists</h3>
          {equipment.checklists.length === 0 ? (
            <EmptyState icon={<ClipboardCheck size={48} />} title="No checklists" message="No checklists are assigned to this equipment." />
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr><th>Name</th><th>Template</th><th>Status</th><th>Progress</th><th>Assigned To</th></tr>
                </thead>
                <tbody>
                  {equipment.checklists.map((cl) => (
                    <tr key={cl.id}>
                      <td><Link to={`/checklists/${cl.id}`} className="font-medium">{cl.name}</Link></td>
                      <td className="text-secondary-color">{cl.templateName}</td>
                      <td><StatusBadge status={cl.status} /></td>
                      <td style={{ minWidth: '120px' }}><ProgressBar value={cl.completionPercentage} size="sm" /></td>
                      <td className="text-secondary-color">{cl.assignedTo || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Issues Tab */}
      {activeTab === 'issues' && (
        <div>
          <h3 className="mb-4">Issues</h3>
          {equipment.issues.length === 0 ? (
            <EmptyState icon={<FileWarning size={48} />} title="No issues" message="No issues logged for this equipment." />
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr><th>Number</th><th>Title</th><th>Type</th><th>Category</th><th>Priority</th><th>Status</th><th>Phase</th></tr>
                </thead>
                <tbody>
                  {equipment.issues.map((issue) => (
                    <tr key={issue.id}>
                      <td className="font-medium text-accent">{issue.issueNumber}</td>
                      <td>{issue.title}</td>
                      <td><span className="badge badge-gray-light">{issue.type}</span></td>
                      <td className="text-secondary-color">{issue.category}</td>
                      <td><PriorityBadge priority={issue.priority} /></td>
                      <td><StatusBadge status={issue.status} /></td>
                      <td className="text-secondary-color">{issue.phase || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  )
}
